<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useExactTimestamp } from 'shared/composables/useExactTimestamp';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  domain: { type: String, default: '' },
  contactsCount: { type: Number, default: 0 },
  avatarUrl: { type: String, default: '' },
  lastActivityAt: { type: [String, Number], default: null },
});

const emit = defineEmits(['showCompany']);

const exactTimestamp = useExactTimestamp();

const { t } = useI18n();

const onClickViewDetails = () => emit('showCompany', props.id);

const displayName = computed(() => props.name || t('COMPANIES.UNNAMED'));

const avatarSource = computed(() => props.avatarUrl || null);

const hasContacts = computed(() => Number(props.contactsCount || 0) > 0);

const contactsCountLabel = computed(() =>
  t('COMPANIES.CONTACTS_COUNT', { n: Number(props.contactsCount || 0) })
);

const formattedLastActivityAt = computed(() => {
  if (!props.lastActivityAt) return '';
  return dynamicTime(props.lastActivityAt);
});
</script>

<template>
  <div
    class="grid items-center w-full gap-3 px-5 py-3 transition-colors duration-100 cursor-pointer grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,2.4fr)_minmax(0,1.7fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_64px] hover:bg-n-slate-2"
    @click="onClickViewDetails"
  >
    <div class="flex items-center min-w-0 gap-3">
      <Avatar
        :username="displayName"
        :src="avatarSource"
        class="shrink-0"
        :name="name"
        :size="36"
        hide-offline-status
      />
      <div class="flex flex-col min-w-0">
        <span class="text-[13.5px] font-semibold truncate text-n-slate-12">
          {{ displayName }}
        </span>
        <span v-if="domain" class="text-xs truncate text-n-slate-10 md:hidden">
          {{ domain }}
        </span>
      </div>
    </div>
    <span
      class="items-center hidden min-w-0 gap-1.5 md:flex text-[13px] text-n-slate-11"
      :title="domain"
    >
      <Icon
        v-if="domain"
        icon="i-lucide-globe"
        size="size-3.5 text-n-slate-10 flex-shrink-0"
      />
      <span class="truncate">{{ domain }}</span>
    </span>
    <span
      class="items-center hidden min-w-0 gap-1.5 md:flex text-[13px] text-n-slate-11"
    >
      <template v-if="hasContacts">
        <Icon
          icon="i-lucide-contact"
          size="size-3.5 text-n-slate-10 flex-shrink-0"
        />
        <span class="truncate">{{ contactsCountLabel }}</span>
      </template>
    </span>
    <span class="hidden md:block text-[13px] truncate text-n-slate-11">
      {{ formattedLastActivityAt }}
    </span>
    <div class="flex items-center justify-end">
      <Button
        icon="i-lucide-arrow-up-right"
        variant="ghost"
        color="slate"
        size="xs"
        class="rounded-lg !text-n-slate-11"
        @click.stop="onClickViewDetails"
      />
    </div>
  </div>
</template>
